import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { Layout, Card, Typography, Space, Button, Alert, Spin } from "antd";
import { ReloadOutlined, HomeOutlined, CheckCircleTwoTone } from "@ant-design/icons";
import "../styles/consultComplete.scss";
import { RoomCacheContext } from "../../context/roomContext";
import type { RoomContext } from "../../context/types";
import { clearConsultEnded, markConsultEnded } from "../zoom/telehealthGuard";

const { Content } = Layout;
const { Title, Text } = Typography;

interface Props {
  onLeaveOrJoinSession: () => Promise<void> | void;
}

/**
 * Reads the persisted consult join payload created in Consult.tsx:
 * sessionStorage key: telehealth_consult_join_v1:${cid}
 */
function getPersistedCidFromStorage(): string | null {
  try {
    // Find any key like telehealth_consult_join_v1:<cid>
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (!k) continue;
      if (k.startsWith("telehealth_consult_join_v1:")) {
        const cid = k.split("telehealth_consult_join_v1:")[1];
        if (cid) return cid;
      }
    }
  } catch {}
  return null;
}

export default function ConsultComplete({ onLeaveOrJoinSession }: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const { setActivateTimer } = useContext(RoomCacheContext) as RoomContext;

  const [status, setStatus] = useState<"idle" | "rejoining" | "error">("idle");
  const [errorText, setErrorText] = useState<string>("");

  // Prefer CID passed via route state; fallback to persisted storage key.
  const cid = useMemo(() => {
    const fromState = (location.state as any)?.cid as string | undefined;
    return fromState || getPersistedCidFromStorage();
  }, [location.state]);

  useEffect(() => {
  if (!cid) return;
    markConsultEnded(cid);
  }, [cid]);

  // Back button behavior: never go back to consult; route to "/" (or close if possible)
  useEffect(() => {
    // Add a guard history entry so "Back" triggers popstate we can intercept.
    window.history.pushState({ guard: true }, "", window.location.href);

    const onPopState = () => {
      // Attempt close (only works if tab was opened by script)
      window.close();
      // Fallback: send user to landing
      navigate("/", { replace: true });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [navigate]);

  const reJoinConsult = async () => {
    setErrorText("");

    if (!cid) {
      setStatus("error");
      setErrorText("Missing consult code. Please request a new consult link.");
      return;
    }

    setStatus("rejoining");

    try {
      // Your existing flags (keep if other parts rely on them)
      sessionStorage.setItem("reloading", "true");
      sessionStorage.setItem("reAdmit", "true");

      // Trigger your session join/leave logic
      await onLeaveOrJoinSession?.();

      // Ensure your waiting room timer / admit logic continues
      setActivateTimer?.(true);
      if (cid) clearConsultEnded(cid);
      navigate(`/${cid}`, { replace: true });
    } catch (e) {
      console.warn("reJoinConsult failed", e);
      setStatus("error");
      setErrorText("Unable to re-join. Please contact the other party to restart the consult.");
    }
  };

  const isRejoining = status === "rejoining";

  return (
    <Layout style={{ minHeight: "100vh", background: "#0b0f1a" }}>
      <Content style={{ display: "grid", placeItems: "center", padding: 18 }}>
        <div style={{ width: "min(720px, 100%)" }}>
          <Card
            bordered={false}
            style={{
              borderRadius: 18,
              background: "rgba(255,255,255,0.06)",
              boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
            bodyStyle={{ padding: 22 }}
          >
            <Space direction="vertical" size={14} style={{ width: "100%" }}>
              <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
                <Space align="center" size={12}>
                  <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 26 }} />
                  <div>
                    <Title level={3} style={{ margin: 0, color: "rgba(255,255,255,0.92)" }}>
                      Consult complete
                    </Title>
                    <Text style={{ color: "rgba(255,255,255,0.68)" }}>
                      You may now close this window.
                    </Text>
                  </div>
                </Space>
              </Space>

              <Alert
                showIcon
                type={status === "error" ? "error" : "info"}
                message={status === "error" ? "Couldn’t re-join" : "Accidental disconnect?"}
                description={
                  status === "error"
                    ? errorText
                    : "If the consult ended unexpectedly, you can attempt to re-join. If it fails, request a new link."
                }
              />

              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  marginTop: 6,
                }}
              >
                <Button
                  type="primary"
                  icon={isRejoining ? undefined : <ReloadOutlined />}
                  onClick={reJoinConsult}
                  disabled={!cid}
                  style={{
                    borderRadius: 12,
                    fontWeight: 700,
                  }}
                >
                  {isRejoining ? (
                    <Space size={10}>
                      <Spin size="small" />
                      Re-joining…
                    </Space>
                  ) : (
                    "Re-join consult"
                  )}
                </Button>
              </div>

              {!cid ? (
                <Text style={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
                  No consult code detected in this session. If you opened this page directly, you may need a new consult link.
                </Text>
              ) : null}
            </Space>
          </Card>
        </div>
      </Content>
    </Layout>
  );
}